import React, { useMemo } from 'react';
import OptimizedChart from '../utils/OptimizedChart';
import { useApexTheme } from '../utils/apexTheme';

const WinLossChart = ({ trades, height = 250 }) => {
  const { colors } = useApexTheme();

  const { componentOptions, series } = useMemo(() => {
    if (!trades || trades.length === 0) return { componentOptions: null, series: [] };

    const winningTrades = trades.filter(t => t.pnl > 0);
    const losingTrades = trades.filter(t => t.pnl <= 0);

    const winCount = winningTrades.length;
    const lossCount = losingTrades.length;
    const totalPnL = trades.reduce((sum, t) => sum + t.pnl, 0);
    const avgWin = winCount > 0 ? winningTrades.reduce((sum, t) => sum + t.pnl, 0) / winCount : 0;
    const avgLoss = lossCount > 0 ? losingTrades.reduce((sum, t) => sum + t.pnl, 0) / lossCount : 0;

    const options = {
      chart: {
        type: 'donut',
        height
      },
      labels: ['Winning Trades', 'Losing Trades'],
      colors: [colors.success || '#4caf50', colors.error || '#f44336'],
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '14px'
              },
              value: {
                show: true,
                fontSize: '16px',
                fontWeight: 'bold'
              },
              total: {
                show: true,
                label: 'Net PnL',
                formatter: () => '₹' + totalPnL.toFixed(0)
              }
            }
          }
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          const count = opts.w.config.series[opts.seriesIndex];
          return count + ' (' + val.toFixed(1) + '%)';
        }
      },
      tooltip: {
        y: {
          formatter: (val, opts) => {
            const isWin = opts.seriesIndex === 0;
            const avg = isWin ? avgWin : avgLoss;
            return `${val} trades (Avg PnL: ₹${avg.toFixed(0)})`;
          }
        }
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center'
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            width: 300
          },
          legend: {
            position: 'bottom'
          }
        }
      }]
    };

    return {
      componentOptions: options,
      series: [winCount, lossCount]
    };
  }, [trades, height, colors]);

  if (!componentOptions) {
    return (
      <div className="flex items-center justify-center text-base-content/60" style={{ height }}>
        No trade data available
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-sm font-bold mb-2">Win/Loss Distribution</h4>
      <OptimizedChart
        componentOptions={componentOptions}
        series={series}
        type="donut"
        height={height}
      />
    </div>
  );
};

export default WinLossChart;
